import { useState, useCallback, useEffect, useRef } from 'react';
import { getUniqueDocumentName } from '../useCreateDocumentFromMessage';

/**
 * Orchestrates document panel interactions and chat-document lifecycle.
 * Centralizes open/close panel logic, document creation, and per-conversation tracking.
 */
export const useDocumentOrchestrator = ({
  agentHook,
  conversationsHook,
  chatDocumentsHook,
  documentPanelHook,
  createDocumentFromMessage,
}) => {
  const [documentCreatedMessageIdsByConversation, setDocumentCreatedMessageIdsByConversation] =
    useState({});

  const openDocumentPanel = useCallback(
    (options = {}) => {
      const { documentId, messageId, title, content } = options;

      if (documentId) {
        const doc = chatDocumentsHook.getDocument(documentId);
        chatDocumentsHook.setCurrentDocument(documentId);
        documentPanelHook.openDocumentPanel({
          title: doc?.title ?? 'Document',
          content: doc?.content ?? '',
          messageId,
        });
      } else if (messageId != null) {
        const conversationId = conversationsHook.conversationId ?? null;
        const existingDoc = chatDocumentsHook.getDocumentByMessageId(messageId, conversationId);
        if (existingDoc) {
          chatDocumentsHook.setCurrentDocument(existingDoc.id);
          documentPanelHook.openDocumentPanel({
            title: existingDoc.title ?? 'Document',
            content: existingDoc.content ?? '',
            messageId,
          });
        } else {
          const docTitle = getUniqueDocumentName(title ?? 'Document');
          const docContent = content ?? '';
          const id = chatDocumentsHook.addDocument({
            title: docTitle,
            content: docContent,
            agentUuid: agentHook.selectedAgentId,
            conversationId,
            messageId,
            module: 'HomePageV2',
          });
          chatDocumentsHook.setCurrentDocument(id);
          documentPanelHook.openDocumentPanel({
            title: docTitle,
            content: docContent,
            messageId,
          });
          createDocumentFromMessage({ title: docTitle, content: docContent }).then(
            (documentUuid) => {
              if (documentUuid && id) chatDocumentsHook.updateDocument(id, { documentUuid });
            }
          );
        }
      } else {
        const docTitle = getUniqueDocumentName(title ?? 'Document');
        const docContent = content ?? '';
        const id = chatDocumentsHook.addDocument({
          title: docTitle,
          content: docContent,
          agentUuid: agentHook.selectedAgentId,
          conversationId: conversationsHook.conversationId ?? null,
          messageId: null,
          module: 'HomePageV2',
        });
        chatDocumentsHook.setCurrentDocument(id);
        documentPanelHook.openDocumentPanel({
          title: docTitle,
          content: docContent,
          messageId: null,
        });
        createDocumentFromMessage({ title: docTitle, content: docContent }).then(
          (documentUuid) => {
            if (documentUuid && id) chatDocumentsHook.updateDocument(id, { documentUuid });
          }
        );
      }
    },
    [
      chatDocumentsHook.getDocument,
      chatDocumentsHook.getDocumentByMessageId,
      chatDocumentsHook.addDocument,
      chatDocumentsHook.updateDocument,
      chatDocumentsHook.setCurrentDocument,
      documentPanelHook.openDocumentPanel,
      agentHook.selectedAgentId,
      conversationsHook.conversationId,
      createDocumentFromMessage,
    ]
  );

  const closeDocumentPanel = useCallback(() => {
    documentPanelHook.closeDocumentPanel();
  }, [documentPanelHook]);

  const prevDocumentPanelOpenRef = useRef(documentPanelHook.isDocumentPanelOpen);
  useEffect(() => {
    const wasOpen = prevDocumentPanelOpenRef.current;
    prevDocumentPanelOpenRef.current = documentPanelHook.isDocumentPanelOpen;
    if (wasOpen && !documentPanelHook.isDocumentPanelOpen) {
      const messageId = documentPanelHook.consumeLastOpenedFromMessageId?.();
      const conversationId = conversationsHook.conversationId;
      if (messageId != null && conversationId) {
        setDocumentCreatedMessageIdsByConversation((prev) => {
          const current = prev[conversationId] || [];
          if (current.includes(messageId)) return prev;
          return { ...prev, [conversationId]: [...current, messageId] };
        });
      }
    }
  }, [
    documentPanelHook.isDocumentPanelOpen,
    documentPanelHook.consumeLastOpenedFromMessageId,
    conversationsHook.conversationId,
  ]);

  const removeDocumentCreatedMessageId = useCallback((conversationId, messageId) => {
    if (!conversationId || !messageId) return;
    setDocumentCreatedMessageIdsByConversation((prev) => {
      const current = prev[conversationId] || [];
      if (!current.includes(messageId)) return prev;
      return { ...prev, [conversationId]: current.filter((id) => id !== messageId) };
    });
  }, []);

  return {
    openDocumentPanel,
    closeDocumentPanel,
    documentCreatedMessageIdsByConversation,
    removeDocumentCreatedMessageId,
  };
};
